import { isInt, isNumber } from "./is";

/**
 * A `Map` with a fixed maximum capacity.
 *
 * Entries are evicted in first-in-first-out (FIFO) order
 * when capacity is exceeded. This means the oldest entry
 * is removed to make space.
 *
 * @template K The type of keys.
 * @template V The type of values.
 *
 * @example
 * const map = new FIFOMap<string, number>(3);
 * map.set("a", 1); // { a: 1 }
 * map.set("b", 2); // { a: 1, b: 2 }
 * map.set("c", 3); // { a: 1, b: 2, c: 3 }
 * map.set("d", 4); // { b: 2, c: 3, d: 4 }
 */
export class BoundedMap<K, V> implements Map<K, V> {
  private _capacity: number;
  private _map: Map<K, V>;

  /**
   * Creates a new instance with an optional capacity.
   *
   * @param capacity (Optional) The maximum number of entries the map can hold. Defaults to `Infinity`.
   */
  constructor(capacity?: number);
  /**
   * Creates a new instance with the entries from the given iterable and optional capacity.
   *
   * @param iterable (Optional) An iterable of key-value pairs to initialize the map with.
   * @param capacity (Optional) The maximum number of entries the map can hold. Defaults to `Infinity`.
   */
  constructor(iterable?: Iterable<[K, V]> | null, capacity?: number);
  /**
   * Creates a new instance from an existing `Map` and an optional capacity.
   *
   * @param map (Optional) A `Map` to initialize the instance with.
   * @param capacity (Optional) The maximum number of entries the map can hold. Defaults to `Infinity`.
   */
  constructor(map?: Map<K, V> | null, capacity?: number);
  /**
   * Internal constructor implementation that handles overloading.
   */
  constructor(
    map?: Iterable<[K, V]> | Map<K, V> | null | number,
    capacity?: number,
  ) {
    if (isNumber(map)) {
      capacity = map;
      map = undefined;
    }

    this._capacity = 0;
    if (map == null) {
      this._map = new Map();
    } else if (map instanceof Map) {
      this._map = map;
    } else {
      this._map = new Map(map);
    }

    this.capacity = capacity ?? Infinity;
  }

  /**
   * The maximum number of entries the map can hold.
   */
  get capacity(): number {
    return this._capacity;
  }

  /**
   *
   * Must be a nonnegative integer or `Infinity`.
   *
   * When reduced, the oldest entries are
   * evicted until the map is within capacity.
   *
   * @throws {RangeError} If an invalid capacity is given.
   */
  set capacity(value: number) {
    // Sanitize input
    if (value !== Infinity && (!isInt(value) || value < 0)) {
      throw new RangeError("Invalid map capacity");
    }

    // Update capacity
    this._capacity = value;

    // Check if size violates new capacity
    if (this.size <= this._capacity) {
      return;
    }

    // Special case; Remove all values
    if (value == 0) {
      this._map.clear();
      return;
    }

    // Reduce size to within capacity
    const iterator = this.keys();
    do {
      this.delete(iterator.next().value!);
    } while (this.size > this._capacity);
  }

  /**
   * Removes all entries from the map.
   */
  clear(): void {
    this._map.clear();
  }

  /**
   * Removes a specified entry from the map.
   *
   * @param key The key of the entry to remove.
   *
   * @returns `true` if the entry was found and removed, `false` otherwise.
   */
  delete(key: K): boolean {
    return this._map.delete(key);
  }

  /**
   * Returns an iterable of [key, value] pairs for every entry in the map.
   *
   * @returns An iterable of [key, value] pairs for every entry in the map.
   */
  entries(): MapIterator<[K, V]> {
    return this._map.entries();
  }

  /**
   * Executes a provided function once for each entry in the map.
   *
   * @param callbackFn The function to execute.
   *
   * @param thisArg (Optional) The value to use as `this` when executing the function.
   */
  forEach(
    callbackFn: (value: V, key: K, map: Map<K, V>) => void,
    thisArg?: unknown,
  ): void {
    this._map.forEach((value, key) => {
      callbackFn.call(thisArg, value, key, this);
    });
  }

  /**
   * Gets the value associated with the given key.
   *
   * @param key The key to check.
   *
   * @returns The value if the key exists, otherwise `undefined`.
   */
  get(key: K): V | undefined {
    return this._map.get(key);
  }

  /**
   * Checks whether the given key exists in the map.
   *
   * @param key The key to check.
   *
   * @returns `true` if the key exists, otherwise `false`.
   */
  has(key: K): boolean {
    return this._map.has(key);
  }

  /**
   * Returns an iterable over the keys in the map.
   *
   * @returns An iterable over the keys in the map.
   */
  keys(): MapIterator<K> {
    return this._map.keys();
  }

  /**
   * Adds a new key-value pair to the map.
   *
   * If a key already exists, it is re-inserted at the newest position.
   *
   * If capacity would be exceeded, the oldest entry is removed.
   *
   * @param key The key to set.
   * @param value The value associated with the key.
   *
   * @returns The map instance.
   */
  set(key: K, value: V): this {
    if (!this.delete(key) && this.size >= this._capacity) {
      this.shift();
    }
    this.set(key, value);
    return this;
  }

  /**
   * Removes and returns the oldest entry in the map.
   *
   * @returns The removed key-value pair, or `undefined` if the map is empty.
   */
  shift(): [K, V] | undefined {
    const iterator = this.entries();
    const next = iterator.next();
    if (next.done) {
      return undefined;
    }
    this.delete(next.value[0]);
    return next.value;
  }

  /**
   * The number of entries in the map.
   */
  get size(): number {
    return this._map.size;
  }

  /**
   * Returns an iterable over the values in the map.
   *
   * @returns An iterable over the values in the map.
   */
  values(): MapIterator<V> {
    return this._map.values();
  }

  /**
   * Returns an iterable over the entries in the map.
   *
   * @returns An iterable over the entries in the map.
   */
  [Symbol.iterator](): MapIterator<[K, V]> {
    return this._map[Symbol.iterator]();
  }

  /**
   * The default string tag.
   */
  get [Symbol.toStringTag](): string {
    return BoundedMap.name;
  }
}
